import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactSelect from 'react-select';
import i18n from '../../utils/i18n';

/**
 * @typedef SelectProps
 * @property {any} className
 * @property {(boolean|string)} error
 * @property {(string|[string])} defaultValue
 * @property {string?} name
 * @property {string?} id
 * @property {bool?} disabled
 * @property {string?} placeholder
 * @property {func} onChange
 * @property {bool} isMulti
 * @property {[{
 *  label: string,
 *  value: string
 * }]} options
 */

/**
 *
 * @param {import('react-select/dist/declarations/src/Select').Props & SelectProps} params
 * @returns
 */

const valueIsHomogenousArray = (value) => arraysIsNotEmpty(value) && (
  value.every(val => typeof val === 'string') ||
  value.every(val => typeof val === 'number')
);

const arraysIsNotEmpty = (options) => Array.isArray(options) && options.length > 0;

const Select = ({
  className,
  error,
  defaultValue,
  name,
  id,
  disabled,
  onChange,
  options,
  noOptionsMessage,
  value,
  isMulti = false,
  ...params
}) => {
  const [internalVal, setInternalVal] = useState('');
  const init = useRef(false);

  useLayoutEffect(() => {
    if (defaultValue === null || defaultValue === undefined) {
      init.current = true;
      return;
    }
    updateInternalValue(defaultValue);
  }, []);


  useEffect(() => {
    if (!init.current) return;
    updateInternalValue(value);
  }, [value]);

  const updateInternalValue = (value) => {
    // Return if not options
    if (!arraysIsNotEmpty(options)) return;

    let newValue = '';

    // if isMulti search for all selected values
    if (isMulti && valueIsHomogenousArray(value)) {
      newValue = options.filter(opt => value.some(v => v == opt.value));
    } else if (!isMulti && ['string', 'number'].includes(typeof value)) {
      newValue = options.find(opt => opt.value === value);
    }

    setInternalVal(newValue || '');
    if (!init) init.current = true;
  };

  const handleOnChange = (val, meta) => {
    setInternalVal(val);
    const value = Array.isArray(val)
      ? val.map(option => option.value)
      : val.value;

    if (onChange) {
      if (name || id) {
        onChange({
          target: {
            name: name || id,
            value
          }
        });
      } else {
        onChange(val, meta);
      }
    }
  };

  return (<ReactSelect
    className={classNames('obviously-select-container', className, { error })}
    classNamePrefix='obviously-select'
    defaultValue={defaultValue}
    value={internalVal}
    isDisabled={disabled}
    onChange={handleOnChange}
    name={name}
    id={id}
    options={options}
    noOptionsMessage={noOptionsMessage}
    isMulti={isMulti}
    isRtl={i18n.dir() === 'rtl'}
    placeholder={i18n.t('common.select')}
    {...params}>
  </ReactSelect>);
};

Select.propTypes = {
  className: PropTypes.string,
  error: PropTypes.bool,
  defaultValue: PropTypes.any,
  placeholder: PropTypes.any,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
    })),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  id: PropTypes.string,
  isMulti: PropTypes.bool,
  noOptionsMessage: PropTypes.func,
  disabled: PropTypes.bool,
  value: PropTypes.any,
};

Select.defaultValue = {
  className: '',
  error: false,
  defaultValue: null,
  placeholder: null,
  options: [],
  onChange: null,
  onBlur: null,
  name: null,
  id: null,
  isMulti: false,
  noOptionsMessage: () => i18n.t('influencers.common.no_options'),
  disabled: false,
  value: null
};

export default Select;
